'use client'
import { useHeaderTheme } from '@/providers/HeaderTheme'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import React, { useEffect, useState } from 'react'

import type { Header, Member } from '@/payload-types'

import { BrandWordmark } from '@/components/ui/BrandWordmark'
import { HeaderNav } from './Nav'
import LogoutButton from '@/app/(frontend)/_components/LogoutButton'
import { LogIn } from 'lucide-react'
import { BuyDriveButton } from '@/components/BuyDriveButton'
import { DiscoverDriveButton } from '@/components/DiscoverDriveButton'

interface HeaderClientProps {
  data: Header
  member: Member | null
  assessmentsEnabled?: boolean
}

export const HeaderClient: React.FC<HeaderClientProps> = ({
  data,
  member,
  assessmentsEnabled = true,
}) => {
  /* Storing the value in a useState to avoid hydration errors */
  const [theme, setTheme] = useState<string | null>(null)
  const { headerTheme, setHeaderTheme } = useHeaderTheme()
  const pathname = usePathname()

  useEffect(() => {
    setHeaderTheme(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    if (headerTheme && headerTheme !== theme) setTheme(headerTheme)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerTheme])

  // Ensure data is always defined to prevent runtime errors
  const safeData = data || { id: 0, navItems: null, updatedAt: null, createdAt: null }

  return (
    <header
      className="px-4 md:px-14 sticky top-0 z-50 border-b-[1px] border-[#1E1E1E] bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/70"
      {...(theme ? { 'data-theme': theme } : {})}
      //data-theme="dark"
    >
      <div className="py-2 grid grid-cols-3 items-center">
        {/* Left: Logo */}
        <div className="w-[130px] sm:w-[150px] md:w-[220px] flex items-center justify-start">
          <Link href="/" className="flex items-center">
            <span className="text-white text-lg md:text-2xl font-bold leading-none flex items-center">
              <BrandWordmark className="leading-none -translate-y-1" />
            </span>
          </Link>
        </div>

        {/* Center: CTA buttons (all unauthenticated pages) */}
        <div className="flex justify-center">
          {!member && (
            <div className="flex items-center gap-3 sm:gap-6 md:gap-16">
              {assessmentsEnabled && (
                <DiscoverDriveButton
                  size="sm"
                  className="w-auto md:w-56 justify-center px-0 sm:px-4 text-sm sm:text-base"
                />
              )}
              <BuyDriveButton
                size="sm"
                className="w-auto md:w-56 justify-center px-0 sm:px-4 text-sm sm:text-base"
                // Defaults to drive-mastery and appends ?buy=1 automatically
              />
            </div>
          )}
        </div>

        {/* Right: Nav + auth controls */}
        <div className="flex items-center gap-4 text-sm justify-end">
          <HeaderNav data={safeData} />
          {member ? (
            <>
              {pathname === '/' ? (
                <Link
                  className="flex gap-2 items-center font-medium text-sm transition hover:opacity-80"
                  href="/dashboard"
                >
                  Dashboard
                  <LogIn size={18} strokeWidth={1.5} />
                </Link>
              ) : (
                <LogoutButton />
              )}
            </>
          ) : (
            <Link
              className="flex gap-1.5 sm:gap-2 items-center font-medium text-sm transition hover:opacity-80"
              href="/login"
            >
              <span className="hidden sm:inline">Login</span>
              <LogIn size={18} strokeWidth={1.5} />
            </Link>
          )}
        </div>
      </div>
    </header>
  )
}
